import {
  getLoggedinUser,
  makeid,
} from "../util";
import { getTokenServerSide } from "../services/access-token-service";
import { sendToSlack } from "../services/contact-service";
import {
  uploadScreenshotBase64,
  uploadToMedia,
} from "../services/media-service";

const errorCache = new Map();
const DEDUPLICATION_WINDOW_MS = 5 * 60 * 1000;
const CLEAN_INTERVAL_MS = 10 * 60 * 1000;

const shouldReportError = (message, stack) => {
  const hash = `${message}|${stack}`.substring(0, 300);
  const now = Date.now();
  const lastTime = errorCache.get(hash);

  if (lastTime && now - lastTime < DEDUPLICATION_WINDOW_MS) return false;

  errorCache.set(hash, now);
  return true;
};

async function getNewToken() {
  let getToken = null;
  try {
    getToken = await getTokenServerSide(makeid(8));
  } catch (err) {
    return null;
  }

  if (!getToken) {
    return null;
  }

  if (!getToken.token) return null;

  const { token: newToken } = getToken;
  return newToken.token;
}

setInterval(() => {
  const now = Date.now();
  for (const [key, time] of errorCache) {
    if (now - time > DEDUPLICATION_WINDOW_MS * 2) {
      errorCache.delete(key);
    }
  }
}, CLEAN_INTERVAL_MS);

const sanitizeHtmlSnippet = (html) => {
  if (!html) return "";
  return html.replace(/value=".*?"/gi, 'value="[REDACTED]"');
};

export const useReportClientError = () => {
  const reportClientError = async (
    error,
    info = {},
    metaData = {},
    token = null
  ) => {
    let errorMessage = "Unknown Error";
    let errorStack = "No stack trace available";
    let errorName = "Unknown";
    let sourceURL = "N/A";
    let filename = "N/A";
    let line = "N/A";
    let column = "N/A";

    const href = typeof window !== "undefined" ? window.location.href : "N/A";
    const referrer =
      typeof document !== "undefined" ? document.referrer : "N/A";
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "N/A";
    const platform =
      typeof navigator !== "undefined" ? navigator.platform : "N/A";
    const language =
      typeof navigator !== "undefined" ? navigator.language : "N/A";

    const session = getLoggedinUser() || {};
    const user = session.user || {};

    const userInfo = {
      sessionId: session.sessionId || "N/A",
      userId: user.userId || "N/A",
      userReadId: user.userReadId || "N/A",
      userName: user.userName || "N/A",
      fullName:
        `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A",
      email: user.email || "N/A",
      fullPhone: user.fullPhone || "N/A",
      imageUrl: user.imageUrl || "N/A",
    };

    if (typeof error === "string") {
      if (error !== "") errorMessage = error;
    } else {
      if (error?.message) errorMessage = error.message;
      if (error?.name) errorName = error.name;
      if (error?.stack) errorStack = error.stack;

      if ("filename" in error) filename = error.filename;
      if ("lineno" in error) line = error.lineno;
      if ("colno" in error) column = error.colno;
      if ("sourceURL" in error) sourceURL = error.sourceURL;

      if (error?.error) {
        const inner = error.error;
        if (inner.message) errorMessage = inner.message;
        if (inner.name) errorName = inner.name;
        if (inner.stack) errorStack = inner.stack;
        if (inner.sourceURL) sourceURL = inner.sourceURL;
      }

      if (error?.reason) {
        const reason = error.reason;
        if (reason.message) errorMessage = reason.message;
        if (reason.name) errorName = reason.name;
        if (reason.stack) errorStack = reason.stack;
      }
    }

    if (errorMessage.length > 1000)
      errorMessage = errorMessage.substring(0, 1000) + "...";
    if (errorStack.length > 3000)
      errorStack = errorStack.substring(0, 3000) + "...";

    const safeStringify = (obj) => {
      try {
        return JSON.stringify(obj, null, 2);
      } catch {
        return "[Could not stringify additional info]";
      }
    };

    if (!shouldReportError(errorMessage, errorStack)) return;

    let lastUserEvent = null;
    let activeElement = null;
    let hoveredElement = null;
    let screenshotBase64 = null;

    if (metaData?.lastUserEvent) {
      lastUserEvent = safeStringify(metaData.lastUserEvent);
    }

    if (metaData?.activeElement) {
      activeElement = sanitizeHtmlSnippet(metaData.activeElement);
    }

    if (metaData?.hoveredElement) {
      hoveredElement = sanitizeHtmlSnippet(metaData.hoveredElement);
    }

    if (metaData?.screenshotBase64) {
      screenshotBase64 = metaData.screenshotBase64;
    }

    const fullInfo = {
      ...info,
      ...userInfo,
      errorName,
      filename,
      sourceURL,
      line,
      column,
      referrer,
      userAgent,
      platform,
      language,
      timestamp: new Date().toISOString(),
    };

    if (info?.type) fullInfo.eventType = info.type;

    const infoStr = safeStringify(fullInfo);
    const truncatedInfo =
      infoStr.length > 3000 ? infoStr.substring(0, 3000) + "..." : infoStr;

    let screenshotUrl = null;
    let jsonUrl = null;

    try {
       if (!token) {
        token = await getNewToken();
       }
      if (token) {
        if (screenshotBase64 && screenshotBase64.startsWith("data:image/")) {
          screenshotUrl = await uploadScreenshotBase64({
            base64: screenshotBase64,
            token,
            folder: "client-errors",
          });
        }

        const logStr = safeStringify({ ...fullInfo, ...metaData });

        const jsonBlob = new Blob([logStr], { type: "application/json" });
        const jsonFile = new File([jsonBlob], `error-${Date.now()}.json`, {
          type: "application/json",
        });

        const uploadJson = await uploadToMedia(
          token,
          jsonFile,
          "client-errors"
        );
        jsonUrl = uploadJson?.response?.media?.url;

        if (!jsonUrl && uploadJson?.error) {
          console.warn("Media upload error:", uploadJson.message);
        }
      }
    } catch (e) {
      console.error("Error uploading media:", e);
    }

    const fields = [
      {
        title: "🧠 Error Message",
        value: `\`\`\`${errorMessage}\`\`\``,
        short: false,
      },
      {
        title: "🪵 Stack Trace",
        value: `\`\`\`${errorStack}\`\`\``,
        short: false,
      },
      {
        title: "👤 User Info",
        value: `ID: ${userInfo.userId}\nEmail: ${userInfo.email}\nFull Name: ${userInfo.fullName}\nUserName: ${userInfo.userName}\nSession: ${userInfo.sessionId}`,
        short: false,
      },
      {
        title: "🌍 Page URL",
        value: href,
        short: false,
      },
      {
        title: "🌐 Referrer",
        value: referrer,
        short: false,
      },
      {
        title: "📱 User Agent",
        value: userAgent,
        short: false,
      },
      {
        title: "🛠 Platform / Language",
        value: `${platform} / ${language}`,
        short: true,
      },
      {
        title: "🕒 Time",
        value: new Date().toLocaleString(),
        short: true,
      },
      {
        title: "📦 Additional Info",
        value: "```" + truncatedInfo + "```",
        short: false,
      },
    ];

    if (fullInfo.eventType) {
      fields.push({
        title: "🧩 Event Type",
        value: fullInfo.eventType,
        short: true,
      });
    }

    if (lastUserEvent) {
      fields.push({
        title: "🖱 Last User Event",
        value: "```" + lastUserEvent + "```",
        short: false,
      });
    }

    if (activeElement) {
      fields.push({
        title: "🔲 Active Element",
        value: "```" + activeElement + "```",
        short: false,
      });
    }

    if (hoveredElement) {
      fields.push({
        title: "🧭 Hovered Element",
        value: "```" + hoveredElement + "```",
        short: false,
      });
    }

    if (metaData?.componentStack) {
      fields.push({
        title: "🧱 React Component Stack",
        value: "```" + metaData?.componentStack + "```",
        short: false,
      });
    }

    if (screenshotUrl) {
      fields.push({
        title: "📸 Screenshot",
        value: `<${screenshotUrl}|View Screenshot>`,
        short: false,
      });
    } else if (screenshotBase64) {
      fields.push({
        title: "📸 Screenshot Upload",
        value: "Screenshot present but upload failed.",
        short: false,
      });
    }

    if (jsonUrl) {
      fields.push({
        title: "📄 Full JSON Report",
        value: `<${jsonUrl}|Download JSON>`,
        short: false,
      });
    }

    if (!screenshotUrl && screenshotBase64) {
      fields.push({
        title: "⚠️ Screenshot Upload Error",
        value: "Upload failed or token missing.",
        short: false,
      });
    }

    const message = {
      text: `🚨 *Client-side Error Detected on Circleplug.com*`,
      attachments: [
        {
          color: "#ff0000",
          title: `[${
            fullInfo.eventType || "Unknown"
          }] ${errorName} @ ${filename}:${line}:${column}`,
          fields,
          footer: "Circleplug Client Error Reporter",
        },
      ],
    };

    sendToSlack(message);
  };

  return reportClientError;
};
